import { useMemo } from "react";
import { useShows } from "../../app/hooks/useShows.js";
import { useShowMoney, showMoneyById } from "../../app/hooks/useMoney.js";
import { useCapability } from "../../app/bandContext.js";
import { pastRowStats, usdCents, signedUsdCents, pnlTone } from "./lib/bookingMoney.js";
import { todayIso } from "./lib/bookingRows.js";
import styles from "./PastShowsCard.module.scss";

const PAST_MAX = 5;

/**
 * "Past shows" — the mobile Schedule's fourth section (spec, "Live specimen · iOS"). Five most recent past
 * shows, each with its date block, venue and city, and — for a MONEY seat only — the Profit / Merch /
 * Total profit / Tickets strip and a tappable actions sheet.
 *
 * MOBILE ONLY by mount (`OpsCalendarScreen`), because the desktop equivalent is Booking's Past tab. Two
 * renderings of one list is how they come to disagree, so this card shares that tab's authorities rather than
 * re-deriving anything: the same money-gated `/shows/money` list, the same `pastRowStats` (Total profit IS the
 * row P&L and Profit is that minus merch), the same `-` for a figure nobody recorded.
 *
 * THE MONEY IS A SEPARATE LIST, NOT A FIELD (the A3 rule). The money-free `/shows` read draws the rows; the
 * money-gated `/shows/money` read is requested over the SAME window and lined up by `showId`. A money-free
 * seat never mounts the second query, so their card is money-free end to end — the request never happens and
 * would 403 if it did.
 *
 * THE NO-MONEY ROW DRAWS NO WRAPPER — that is the spec's own listed defect, fixed here rather than ported:
 * legacy always rendered `.sc-past__stats` and gated only its children, so a no-money seat got the wrapper's
 * `border-top` plus ~17px of dead space under the city line with nothing beneath it. It is also inert — no
 * `onClick`, no pointer cursor, no `role="button"` — because the only thing behind the row is money work.
 *
 * Returns null when there is nothing behind it, per legacy: on mobile this section and Coming Up simply
 * disappear rather than printing an empty state, and a first-run Schedule ends after the day line.
 */
export function PastShowsCard({ onOpenShow }: {
  /** Open the show's event sheet (ART-305 v3) — the money view's Settlement, Outstanding Payments and Money. */
  onOpenShow: (showId: string, anchor: HTMLElement) => void;
}) {

  // The same window and sort the Booking Past tab uses: strictly before today (device-local — a UTC "today"
  // rolls over at 19:00 CDT and would file tonight's show under Past while the band is loading in), newest first.
  const to = useMemo(() => todayIso(-1), []);
  const shows = useShows({ to, sort: "-date" });

  const canFullMoney = useCapability("money:full");
  const canPerShowMoney = useCapability("money:per_show");
  const canSeeMoney = canFullMoney || canPerShowMoney;
  const showMoney = useShowMoney({ to, limit: PAST_MAX }, canSeeMoney);
  const money = useMemo(
    () => (canSeeMoney ? showMoneyById(showMoney.data) : undefined),
    [canSeeMoney, showMoney.data],
  );

  const rows = useMemo(() => (shows.data?.items ?? []).slice(0, PAST_MAX), [shows.data]);
  if (rows.length === 0) return null;

  return (
    <section className={styles.card} aria-labelledby="past-shows-heading">
      <p id="past-shows-heading" className={styles.eyebrow}>Past shows</p>
      <ul className={styles.list}>
        {rows.map((show) => {
          const showId = show.showId!;
          const stats = canSeeMoney ? pastRowStats(money?.get(showId)) : null;
          const d = new Date(`${(show.date ?? "").slice(0, 10)}T12:00:00`);
          const label = show.venueName || show.city || "Untitled show";
          const body = (
            <>
              <span className={styles.date}>
                <span className={styles.mon}>{d.toLocaleDateString(undefined, { month: "short" }).toUpperCase()}</span>
                <span className={styles.num}>{String(d.getDate()).padStart(2, "0")}</span>
              </span>
              <span className={styles.main}>
                <span className={styles.venue}>{label}</span>
                {show.city && show.city !== label && <span className={styles.city}>{show.city}</span>}
              </span>
            </>
          );
          return (
            <li key={showId} className={styles.row}>
              {/* A money seat's row opens the show's event sheet (ART-305 v3 — it replaced the old "Settle up /
                  Expenses" menu); a money-free seat's row is a plain block, with nothing behind it. */}
              {stats ? (
                <button type="button" className={styles.rowBtn} onClick={(e) => onOpenShow(showId, e.currentTarget)}>
                  {body}
                </button>
              ) : (
                <div className={styles.rowStatic}>{body}</div>
              )}
              {stats && (
                <span className={styles.stats}>
                  <Stat label="Profit" value={signedUsdCents(stats.profitCents)} tone={pnlTone(stats.profitCents)} />
                  <Stat label="Merch" value={stats.merchCents != null ? usdCents(stats.merchCents) : "-"} />
                  <Stat label="Total profit" value={signedUsdCents(stats.totalProfitCents)} tone={pnlTone(stats.totalProfitCents)} />
                  <Stat label="Tickets" value={stats.ticketsSold != null ? String(stats.ticketsSold) : "-"} />
                </span>
              )}
            </li>
          );
        })}
      </ul>

    </section>
  );
}

/** One label-over-value figure in the strip. Mirrors the Booking Past row's `Figure`, at the mobile scale. */
function Stat({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  return (
    <span className={styles.stat}>
      <span className={styles.statLabel}>{label}</span>
      <span className={styles.statValue} data-tone={tone}>{value}</span>
    </span>
  );
}
